import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Cart,
  Language,
  Category,
  PhoneHeader,
  EmailHeader,
} from "../../assets/icons/icons";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import CategoriesMenu from "../sections/CategoriesMenu";
import LangRes from "../sections/LangRes";
import SearchSection from "../sections/SearchSection";

export default function Header() {
  const { t, i18n } = useTranslation();

  const [isOpenLanguage, setIsOpenLanguage] =
    useState(false);

  const [isScrolled, setIsScrolled] =
    useState(false);

  const dropdownRef = useRef(null);

  /* =========================
     Scroll Header
  ========================= */

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener(
      "scroll",
      handleScroll
    );

    handleScroll();

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  /* =========================
     Language
  ========================= */

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpenLanguage(false);
  };

  /* =========================
     Close Language Outside
  ========================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target
        )
      ) {
        setIsOpenLanguage(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /* =========================
     RTL / LTR
  ========================= */

  useEffect(() => {
    const updateDirection = (lng) => {
      document.documentElement.lang = lng;

      document.documentElement.dir =
        lng === "ar"
          ? "rtl"
          : "ltr";
    };

    updateDirection(i18n.language);

    i18n.on(
      "languageChanged",
      updateDirection
    );

    return () => {
      i18n.off(
        "languageChanged",
        updateDirection
      );
    };
  }, [i18n]);

  return (
    <header
      className={`
        shadow-custom
        top-0
        left-0
        w-full
        z-50
        transition-all
        duration-300

        ${
          isScrolled
            ? "fixed bg-white shadow-lg"
            : "bg-transparent text-black"
        }
      `}
    >
      {/* ========================= */}
      {/* Top Header */}
      {/* ========================= */}

      <div className="d-none py-[10px] bg-[#F5F5F5]">
        <div className="
          container
          flex
          items-center
          justify-between
        ">
          {/* Social Media */}
          <div className="
            d-none
            flex
            items-center
            gap-[5px]
          ">
            <a
              href="https://www.facebook.com/people/AJ-Mart/61573171957943/"
              target="_blank"
              rel="noreferrer"
            >
              <img
                className="w-[20px] h-[20px]"
                src={
                  require(
                    "../../assets/icons/facebook.svg"
                  ).default
                }
                alt="Facebook"
              />
            </a>

            <a
              href="https://www.instagram.com/aj_mart.jo/?hl=en"
              target="_blank"
              rel="noreferrer"
            >
              <img
                className="w-[20px] h-[20px]"
                src={
                  require(
                    "../../assets/icons/instagram.svg"
                  ).default
                }
                alt="Instagram"
              />
            </a>

            <a
              href="https://wa.link/ppk1jv"
              target="_blank"
              rel="noreferrer"
            >
              <img
                className="w-[20px] h-[20px]"
                src={
                  require(
                    "../../assets/icons/Whatsapp.svg"
                  ).default
                }
                alt="Whatsapp"
              />
            </a>
          </div>

          {/* Contact */}
          <div className="
            flex
            items-center
            gap-[10px]
            font-15
          ">
            <a
              href="mailto:info@aj-mart.com"
              className="
                flex
                items-center
                gap-[3px]
              "
            >
              <EmailHeader />

              <span
                dir="ltr"
                className="inline-block"
              >
                info@aj-mart.com
              </span>
            </a>

            <a
              href="tel:+962790773377"
              className="
                flex
                items-center
                gap-[3px]
              "
            >
              <PhoneHeader />

              <span
                dir="ltr"
                className="inline-block"
              >
                +962 79 077 3377
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="
        bg-[#F5F5F5]
        h-[2px]
      " />

      {/* ========================= */}
      {/* Main Header */}
      {/* ========================= */}

      <div
        className="
          container
          flex
          items-center
          justify-between
          py-[20px]
          gap-4
        "
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex-shrink-0"
        >
          <img
            className="w-[125px]"
            src={
              require(
                "../../assets/logo/logo.svg"
              ).default
            }
            alt="Logo"
          />
        </Link>

        {/* ========================= */}
        {/* Desktop Search */}
        {/* ========================= */}

        <div className="d-none flex-1 flex justify-center">
          <SearchSection desktop />
        </div>

        {/* ========================= */}
        {/* Desktop Right Menu */}
        {/* ========================= */}

        <div className="
          d-none
          flex
          gap-4
          items-center
          flex-shrink-0
        ">
          {/* Categories */}
          <div className="
            flex
            items-center
            gap-[3px]
          ">
            <Category />
            <CategoriesMenu />
          </div>

          {/* Cart */}
          <Link
            className="
              flex
              items-center
              text-[#666666]
              font-bold
              gap-[3px]
            "
            to="/cart"
          >
            <Cart />

            <span>
              {t("Cart")}
            </span>
          </Link>

          {/* Language */}
          <div
            className="relative"
            ref={dropdownRef}
          >
            <button
              type="button"
              onClick={() =>
                setIsOpenLanguage(
                  (prev) => !prev
                )
              }
              className="
                flex
                items-center
                gap-2
                p-2
                text-[#666666]
                font-bold
                rounded-md
              "
            >
              <Language />

              <span>
                {t("Language")}
              </span>
            </button>

            {isOpenLanguage && (
              <div
                className="
                  absolute
                  right-0
                  mt-2
                  w-40
                  bg-white
                  shadow-lg
                  rounded-md
                "
                style={{
                  zIndex: 1000,
                }}
              >
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() =>
                      changeLanguage("en")
                    }
                    className="
                      w-full
                      text-left
                      px-4
                      py-2
                      text-[#666666]
                      hover:bg-gray-200
                      rounded-t-md
                    "
                  >
                    English
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      changeLanguage("ar")
                    }
                    className="
                      w-full
                      text-left
                      px-4
                      py-2
                      text-[#666666]
                      hover:bg-gray-200
                      rounded-b-md
                    "
                  >
                    العربية
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ========================= */}
        {/* Mobile */}
        {/* ========================= */}

        <div className="
          dis-none
          flex
          items-center
          gap-[5px]
        ">
          <div className="
            flex
            items-center
            gap-[5px]
          ">
            <SearchSection />

            <Link
              className="
                flex
                items-center
                text-[#666666]
                font-bold
                gap-[3px]
              "
              to="/cart"
            >
              <Cart />
            </Link>

            <LangRes />

            <Link to="/category">
              <Category />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}